import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const Support = () => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Populate form with user data when component mounts or user changes
  useEffect(() => {
    if (user) {
      // Extract first and last name from displayName
      const fullName = user.displayName || '';
      
      setFormData(prevData => ({
        ...prevData,
        name: fullName,
        email: user.email || ''
      }));
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccessMessage(''); // Clear any previous messages
    setErrorMessage('');

    try {
      // Get API URL from environment variable or use default
      const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
      
      const response = await axios.post(`${API_URL}/api/contact`, formData);

      if (response.data.success) {
        setSuccessMessage(response.data.message || 'Thank you for reaching out! Our support team will get back to you shortly.');
        setFormData(prevData => ({
          ...prevData,
          subject: '',
          message: ''
        })); // Clear form but keep name and email
      } else {
        throw new Error(response.data.message || 'Failed to send email');
      }
    } catch (error) {
      console.error('There was an error sending the email:', error);
      setErrorMessage(error.response?.data?.message || error.message || 'Failed to send email. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="mt-6 text-center mb-12 max-w-3xl mx-auto">
        <h2 className="text-2xl font-bold">Help & Support</h2>
        <p className="text-gray-600 text-center mt-4 text-md">
          If you have any questions or encounter any issues, please fill out the form below and our support team will get back to you as soon as possible.
        </p>
      </div>
      <div className="flex justify-center items-center">
        <div className="w-full max-w-3xl">
          <form 
            onSubmit={handleSubmit} 
            className="bg-white border rounded-lg px-8 pt-6 pb-8 mb-4"
          >      
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">     
              <div className="mb-4">
                <label 
                  className="block text-gray-700 text-sm font-bold mb-2" 
                  htmlFor="name"
                >
                  Name
                </label>
                <input  
                  className="appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline" 
                  id="name" 
                  type="text" 
                  name="name" 
                  value={formData.name} 
                  onChange={handleChange} 
                  required 
                />
              </div>
             
              <div className="mb-4">
                <label 
                  className="block text-gray-700 text-sm font-bold mb-2" 
                  htmlFor="email"
                >
                  Email
                </label>
                <input 
                  className="appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline" 
                  id="email" 
                  type="email" 
                  name="email" 
                  value={formData.email} 
                  onChange={handleChange} 
                  required 
                />
              </div>
            </div>
            <div className="mb-4">
              <label 
                className="block text-gray-700 text-sm font-bold mb-2" 
                htmlFor="subject"
              >
                Subject
              </label>
              <input 
                className="appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline" 
                id="subject" 
                type="text" 
                name="subject" 
                value={formData.subject} 
                onChange={handleChange} 
                required 
              />
            </div>         
            <div className="mb-6">
              <label 
                className="block text-gray-700 text-sm font-bold mb-2" 
                htmlFor="message"
              >
                Message
              </label>
              <textarea 
                className="appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline h-32 resize-none" 
                id="message" 
                name="message" 
                value={formData.message} 
                onChange={handleChange} 
                required 
              />
            </div>
            <div className="flex items-center justify-between">
              <button 
                className={`btn-theme text-white py-2 px-4 rounded focus:outline-none focus:shadow-outline ${
                  loading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                type="submit"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </span>
                ) : (
                  'Submit Request'
                )}
              </button>
            </div>
          </form>
          {successMessage && (
            <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4 rounded">
              <p className="font-semibold">{successMessage}</p>
            </div>
          )}
          {errorMessage && (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4 rounded">
              <p className="font-semibold">{errorMessage}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Support;